import { useEffect, useMemo } from 'react';
import Select from '@Common/Select';
import { JiraProject } from '@Models/types';
import { useAuth } from '@Context/CognitoAuthProvider';
import { jiraAPI } from '@Lib/axios/jira';
import Tabs from './Tabs';
import { useAnalyzerContext } from '@Context/AnalyzerContext';
import FilterPods from '@Components/analyzer/controls/FilterPods';
import { Tooltip } from 'react-tooltip';
import { Warning } from '@phosphor-icons/react';
import colors from '@Common/assets/colors';

const warningColorMap: Record<number, string> = {
  3: '#FF7F50',
  4: '#FF6347',
  5: '#FF0000',
};

interface JiraProjectsProps {
  onModeSwitch?: () => void;
  disabled: boolean;
}

const JiraProjects = ({ onModeSwitch, disabled }: JiraProjectsProps) => {
  const { user, logout } = useAuth();
  const jira = useMemo(() => jiraAPI(logout), [logout]);
  const {
    projects,
    fetchProjects,
    selectedProject,
    setSelectedProject,
    selectedProjects,
    setSelectedProjects,
    loadingProj,
    setLoadingProj,
    selectedDateFilter,
    setSelectedDateFilter,
    filterStartDate,
    setFilterStartDate,
    filterEndDate,
    setFilterEndDate,
    showFilter,
    setShowFilter,
    globalLoading,
    removeSelectedProject,
  } = useAnalyzerContext();

  const loading = loadingProj;

  useEffect(() => {
    // Only fetch if the user is authenticated and the list is empty
    if (user?.profile && projects.length === 0) {
      setLoadingProj(true);
      fetchProjects().finally(() => setLoadingProj(false));
    }
  }, [user, projects.length, setLoadingProj]);

  const projectOptions = useMemo(
    () =>
      projects.map((proj) => ({
        label: `${proj.name} (${proj.key})`,
        value: proj,
      })),
    [projects]
  );

  // 2. set selected projects (up to five)
  const handleProjectChange = (
    selectedOption: { label: string; value: JiraProject } | null
  ) => {
    if (selectedOption) {
      if (selectedProjects.length >= 5) {
        console.warn('Maximum 5 selections allowed');
        return;
      }
      const project = selectedOption.value;
      if (!selectedProjects.find((p) => p.key === project.key)) {
        setSelectedProjects([...selectedProjects, project]);
      }
      setSelectedProject(null);
    }
  };

  const placeholder =
    loading && !selectedProject
      ? 'Loading Jira Spaces...'
      : selectedProject
        ? `${selectedProject.name} (${selectedProject.key})`
        : 'Select Jira Project';

  // define favorites
  const favorites = useMemo(
    () => ({
      enabled: true,
      type: 'jira-project' as const,
      fetch: jira.getFavProjects,
      save: jira.saveFavProjects,
      favoriteKey: 'key' as const,
    }),
    [jira]
  );

  return (
    <div data-testid="test-jira-projects" className="rounded-lg bg-white p-0">
      {/* Main Container: Left Col (Tabs) + Right Col (Everything Else) */}
      <div className="flex w-full items-start gap-x-3 pl-1">
        {/* LEFT COLUMN: Tabs */}
        {/* pt-1 or mt-1 might be needed here to perfectly center-align the tab text with the Select input text, depending on your Select component's height */}
        <div className="shrink-0 pt-1">
          <Tabs
            onModeSwitch={onModeSwitch}
            isJiraActive={true}
            disabled={disabled}
          />
        </div>

        {/* RIGHT COLUMN: Controls (Top) + Selected Items (Bottom) */}
        <div className="flex flex-1 flex-col gap-y-2">
          {/* Right Col - Top Row: Select + Counter + Filter */}
          <div className="flex w-full items-center gap-x-2">
            <div className="w-[400px] shrink-0">
              <Select
                type="jira-project"
                options={projectOptions}
                onChange={handleProjectChange}
                placeholder={placeholder}
                loading={loading}
                disabled={loading || disabled}
                favorites={favorites}
              />
            </div>

            <div className="flex shrink-0 items-center gap-x-2">
              {selectedProjects.length <= 5 && (
                <div className="flex items-center whitespace-nowrap rounded bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-600">
                  {selectedProjects.length}/5
                </div>
              )}

              <FilterPods
                selectedDateFilter={selectedDateFilter}
                setSelectedDateFilter={setSelectedDateFilter}
                filterStartDate={filterStartDate}
                setFilterStartDate={setFilterStartDate}
                filterEndDate={filterEndDate}
                setFilterEndDate={setFilterEndDate}
                setShowFilter={setShowFilter}
                showFilter={showFilter}
                disabled={globalLoading}
              />

              {selectedProjects.length >= 3 && selectedProjects.length <= 5 && (
                <span
                  data-tooltip-id="pod-warning"
                  data-tooltip-content={`Activating ${selectedProjects.length} pods may take a while to process.\nPerformance may be affected.\nSome tickets may not be classified properly.`}
                  data-tooltip-place="top"
                  className="cursor-help"
                >
                  <Warning
                    size={24}
                    color={warningColorMap[selectedProjects.length]}
                    weight="fill"
                  />
                </span>
              )}
            </div>
          </div>

          {/* Right Col - Bottom Row: Selected Project Tags */}
          {/* Because this is inside the Right Column, it will never cross under the Tabs */}
          {selectedProjects.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedProjects.map((proj) => (
                <div
                  key={proj.key}
                  className="flex items-center gap-x-1.5 rounded bg-gray-200 px-2 py-1 text-xs font-semibold text-gray-700"
                >
                  {proj.name}
                  <button
                    onClick={() => removeSelectedProject(proj.key || '')}
                    className="font-bold text-red-500 hover:text-red-700"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Tooltip
        id="pod-warning"
        style={{
          color: 'white',
          background: colors.primary[700],
          borderRadius: '0.5rem',
        }}
        className="font-medium"
      />
    </div>
  );
};

export default JiraProjects;
